import { Link } from "react-router-dom";
import { ArrowRight, Handshake, IndianRupee } from "lucide-react";
import { GALLERY } from "../data/site";
import { Reveal } from "../components/Reveal";
import { usePageMeta } from "../hooks/usePageMeta";

const VALUES = [
  {
    title: "Natural product alternatives",
    desc: "Leaf plates, bowls, thalis and grass straws made from natural materials — a practical swap for conventional disposables.",
  },
  {
    title: "Plastic-free product design",
    desc: "Designed without plastic, as described in our business proposal for cleaner, greener food service.",
  },
  {
    title: "Thoughtful construction",
    desc: "A dual-layer build using biodegradable cardboard and glue, made for dependable everyday use.",
  },
  {
    title: "People behind the product",
    desc: "Our work supports tribal women's livelihoods and keeps traditional craftsmanship alive.",
  },
  {
    title: "Access that keeps growing",
    desc: "A long-term commitment to making eco-friendly products available to more businesses across India.",
  },
];

const AUDIENCES = [
  "Dealers & Distributors",
  "Wholesalers & Retailers",
  "Event Organisers & Caterers",
  "Hotels, Restaurants & Cafes",
  "Bulk Buyers & White-Label Partners",
];

export default function Home() {
  usePageMeta(
    "ORGANIKTHINGS LLP | Natural Alternatives to Single-Use Plastic",
    "Organik Things LLP makes natural leaf plates, bowls, thalis and grass straws — eco-friendly alternatives for dealers, distributors, caterers and bulk buyers across India. Est. 2021."
  );
  return (
    <main data-testid="home-page">
      {/* ── Hero ─────────────────────────────────────────── */}
      <section className="bg-[#0f2a1c] text-white overflow-hidden" data-testid="hero-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 lg:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="enter text-xs font-semibold tracking-[0.2em] uppercase text-[#95d5b2] mb-6">
              Organik Things LLP · Est. 2021 · Indore, India
            </p>
            <h1 className="enter enter-d1 font-heading text-4xl sm:text-5xl lg:text-[3.6rem] font-extrabold tracking-tight leading-[1.08]">
              The future is<br />not plastic.
            </h1>
            <p className="enter enter-d2 mt-4 font-heading text-xl sm:text-2xl font-semibold text-[#b7e4c7]">
              Small Changes. A Greener Tomorrow.
            </p>
            <p className="enter enter-d2 mt-5 text-base md:text-lg text-white/70 leading-relaxed max-w-xl">
              Discover natural alternatives to conventional single-use
              disposables with Organik Things LLP. We help businesses explore
              more sustainable choices for everyday food service.
            </p>
            <div className="enter enter-d3 mt-9 flex flex-col sm:flex-row gap-3">
              <a
                href="#problem"
                data-testid="hero-cta-mission"
                className="inline-flex justify-center items-center gap-2 bg-[#d8f3dc] hover:bg-white text-[#0f2a1c] font-semibold px-7 py-3.5 rounded-lg transition-colors"
              >
                Explore Our Mission <ArrowRight className="w-4 h-4" />
              </a>
              <Link
                to="/dealership"
                data-testid="hero-cta-partner"
                className="inline-flex justify-center items-center gap-2 border border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-semibold px-7 py-3.5 rounded-lg transition-colors"
              >
                <Handshake className="w-5 h-5" /> Partner With Us
              </Link>
            </div>
          </div>
          {/* Product composition — real photographs */}
          <div className="enter enter-d2 relative h-72 sm:h-96 lg:h-[460px]" data-testid="hero-image">
            <div className="absolute right-0 top-0 w-56 h-56 sm:w-72 sm:h-72 lg:w-80 lg:h-80 rounded-full overflow-hidden ring-8 ring-white/10 shadow-2xl animate-float">
              <img src={GALLERY.setNested} alt="Nested set of Organik Things leaf plates and bowls" className="w-full h-full object-cover" />
            </div>
            <div className="absolute left-2 sm:left-6 bottom-2 w-36 h-36 sm:w-48 sm:h-48 rounded-full overflow-hidden ring-8 ring-white/10 shadow-2xl animate-float-delayed">
              <img src={GALLERY.bowlDeep} alt="Natural green leaf bowl by Organik Things" className="w-full h-full object-cover" />
            </div>
            <div className="absolute left-8 sm:left-16 top-2 w-24 h-24 sm:w-32 sm:h-32 rounded-full overflow-hidden ring-8 ring-white/10 shadow-xl animate-float">
              <img src={GALLERY.plateGreen} alt="Natural green leaf plate by Organik Things" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* ── The problem ──────────────────────────────────── */}
      <section id="problem" className="bg-white py-16 md:py-24" data-testid="problem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#2d6a4f] mb-4">The Problem</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0d1f18] leading-tight">
                Convenience shouldn't cost our planet.
              </h2>
            </Reveal>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16">
            <Reveal>
              <p className="text-base md:text-lg text-[#2d3748] leading-relaxed">
                Single-use disposables are designed for minutes of use. Yet when
                plastic plates, bowls and straws are used at scale and disposed
                of carelessly, they can contribute to litter, pollution and
                long-term environmental harm — in our streets, soil and water.
              </p>
            </Reveal>
            <Reveal delay={100}>
              <p className="text-base md:text-lg text-[#2d3748] leading-relaxed">
                For businesses that serve food every day — caterers, hotels,
                restaurants and event organisers — the choice of disposable
                tableware adds up quickly. Choosing what we serve on is one of
                the simplest places to begin doing better.
              </p>
            </Reveal>
          </div>
          <Reveal>
            <div className="mt-12 border-l-4 border-[#2d6a4f] bg-[#f4f1ea] px-6 py-5 max-w-3xl">
              <p className="text-base md:text-lg font-medium text-[#0d1f18] leading-relaxed">
                Around 400 million tonnes of plastic waste are produced
                worldwide every year.
              </p>
              <p className="mt-1 text-xs text-[#5a6b5c] tracking-wide">
                Source: United Nations Environment Programme (UNEP)
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Our alternative ──────────────────────────────── */}
      <section className="bg-[#f4f1ea] py-16 md:py-24" data-testid="solution-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#2d6a4f] mb-4">Our Alternative</p>
                <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0d1f18] leading-tight">
                  A more natural way to serve.
                </h2>
                <p className="mt-5 text-base md:text-lg text-[#2d3748] leading-relaxed max-w-xl">
                  Organik Things focuses on natural leaf plates, bowls, thalis
                  and grass straws — simple, biodegradable alternatives to
                  conventional disposable products, made for real food service.
                </p>
                <Link
                  to="/products"
                  data-testid="solution-cta-products"
                  className="mt-8 inline-flex items-center gap-2 bg-[#1b4332] hover:bg-[#2d6a4f] text-white font-semibold px-7 py-3.5 rounded-lg transition-colors"
                >
                  Explore Our Products <ArrowRight className="w-4 h-4" />
                </Link>
              </Reveal>
            </div>
            <Reveal delay={100}>
              <div className="flex items-end justify-center gap-3 sm:gap-6">
                {[
                  { src: GALLERY.plateFresh, label: "Leaf Plates", size: "w-24 h-24 sm:w-40 sm:h-40" },
                  { src: GALLERY.bowlSmall, label: "Leaf Bowls", size: "w-28 h-28 sm:w-48 sm:h-48 -translate-y-4" },
                  { src: GALLERY.plateLarge, label: "Leaf Thalis", size: "w-24 h-24 sm:w-40 sm:h-40" },
                ].map((p) => (
                  <figure key={p.label} className="text-center">
                    <div className={`${p.size} rounded-full overflow-hidden ring-4 ring-white shadow-lg mx-auto`}>
                      <img src={p.src} alt={`${p.label} by Organik Things`} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <figcaption className="mt-3 text-xs font-semibold tracking-wide text-[#2d6a4f] uppercase">{p.label}</figcaption>
                  </figure>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Why Organik Things ───────────────────────────── */}
      <section className="bg-white py-16 md:py-24" data-testid="why-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <Reveal>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#2d6a4f] mb-4">Why Organik Things</p>
              <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#0d1f18] leading-tight">
                Business values, not buzzwords.
              </h2>
              <div className="mt-10" data-testid="stat-pieces-sold">
                <p className="font-heading text-5xl sm:text-6xl font-extrabold text-[#1b4332]">800,000+</p>
                <p className="mt-2 text-sm text-[#5a6b5c] leading-relaxed max-w-xs">
                  Eco-friendly pieces sold so far — a company-reported figure
                  since our establishment in 2021.
                </p>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 60}>
                <div className="flex gap-5 sm:gap-8 py-5 border-t border-[#e2ded4] last:border-b" data-testid={`value-row-${i}`}>
                  <span className="font-heading text-sm font-bold text-[#95d5b2] pt-1 shrink-0 w-8">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg sm:text-xl font-bold text-[#0d1f18]">{v.title}</h3>
                    <p className="mt-1.5 text-sm sm:text-base text-[#5a6b5c] leading-relaxed">{v.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Human impact ─────────────────────────────────── */}
      <section className="bg-[#f4f1ea] py-16 md:py-24" data-testid="impact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <img
              src={GALLERY.thaliSpread}
              alt="Leaf thalis and bowls serving full Indian meals"
              className="w-full h-72 sm:h-96 object-cover rounded-2xl"
              loading="lazy"
            />
          </Reveal>
          <div>
            <Reveal delay={80}>
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#2d6a4f] mb-4">Our Vision</p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#0d1f18] leading-tight">
                Better choices. Broader impact.
              </h2>
              <p className="mt-5 text-base md:text-lg text-[#2d3748] leading-relaxed max-w-xl">
                Behind every natural product is a person whose work matters.
                Our model supports tribal women's livelihoods and traditional
                craftsmanship — and our ambition is to expand our dealer network
                across India, making natural alternatives more accessible to
                businesses everywhere.
              </p>
              <Link
                to="/about"
                data-testid="impact-cta-about"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#1b4332] border border-[#1b4332]/30 rounded-lg px-6 py-3 hover:bg-[#1b4332] hover:text-white transition-colors"
              >
                More About Us <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── Partnership ──────────────────────────────────── */}
      <section className="bg-[#0f2a1c] text-white py-16 md:py-24" data-testid="partnership-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[#95d5b2] mb-4">Business Partnership</p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
              Be part of the change.
            </h2>
            <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Join Organik Things in bringing natural product alternatives to
              more businesses and communities.
            </p>
            <ul className="mt-8 flex flex-wrap justify-center gap-2.5" data-testid="partnership-audiences">
              {AUDIENCES.map((a) => (
                <li key={a} className="text-xs sm:text-sm font-medium text-[#d8f3dc] border border-white/20 rounded-full px-4 py-2">
                  {a}
                </li>
              ))}
            </ul>
            <div className="mt-10 flex flex-col sm:flex-row justify-center gap-3">
              <Link
                to="/dealership"
                data-testid="partnership-cta-dealer"
                className="inline-flex justify-center items-center gap-2 bg-[#d8f3dc] hover:bg-white text-[#0f2a1c] font-semibold px-8 py-3.5 rounded-lg transition-colors"
              >
                <Handshake className="w-5 h-5" /> Become a Dealer
              </Link>
              <Link
                to="/contact"
                data-testid="partnership-cta-pricing"
                className="inline-flex justify-center items-center gap-2 border border-white/30 hover:border-white/60 hover:bg-white/5 text-white font-semibold px-8 py-3.5 rounded-lg transition-colors"
              >
                <IndianRupee className="w-4 h-4" /> Request Bulk Pricing
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
